import { useState, useCallback, useContext } from 'react';
import { Dialog, DialogContent, IconButton, Stack, Typography, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DynamicOption } from '@refineintel/types';
import { useQueryGetDynamicOptions } from '@hooks/useQueryGetDynamicOptions';
import { RDropdown } from '@components/RAutocomplete/RDropdown';
import { InvestigationContext } from '@context/InvestigationContext';
import { LocalizationContext } from '@context/LocalizationContext';

const DEFAULT_PAGE_SIZE = 20;
const NAICS_LINK = 'https://www.naics.com/search/';

interface Props {
	questionText: string;
	setCurrentAnswer: (answerId: string | null, answerText?: string) => void;
	isSelectInvalid: boolean;
	initialValue?: {
		naicsCode: DynamicOption | null;
		description: string;
	};
}

export const NAICSQuestion = ({ questionText, setCurrentAnswer, isSelectInvalid, initialValue }: Props) => {
	const [selectedNAICS, setSelectedNAICS] = useState(initialValue?.naicsCode || null);
	const [modalOpen, setModalOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const {
		NAICS: {
			DropdownPlaceholder,
			InformationDialogText,
			InformationDialogTitle,
			DropdownValidationErrorMessage,
			FindYourNAICSCodeButton,
		},
	} = useContext(LocalizationContext);
	const { userLanguage } = useContext(InvestigationContext);

	const [pagination, setPagination] = useState({
		currentPage: 1,
		pageSize: DEFAULT_PAGE_SIZE,
	});

	const { options, isLoading, hasMorePages, paginationInfo } = useQueryGetDynamicOptions({
		searchQuery,
		type: 'naics',
		pagination,
		lang: userLanguage,
	});

	const handleNAICSChange = useCallback(
		(value: DynamicOption | null) => {
			setSelectedNAICS(value);
			setCurrentAnswer(value?.id || null);
		},
		[setCurrentAnswer],
	);

	const handleLoadMore = useCallback(() => {
		if (hasMorePages && !isLoading) {
			setPagination((prev) => ({
				...prev,
				currentPage: paginationInfo.currentPage + 1,
			}));
		}
	}, [hasMorePages, isLoading, paginationInfo.currentPage]);

	const handleSearch = useCallback((value: string) => {
		setSearchQuery(value);
		setCurrentAnswer(null);
		setPagination({
			currentPage: 1,
			pageSize: DEFAULT_PAGE_SIZE,
		});
	}, []);

	return (
		<Stack data-cy={'pqNAICSQuestionComponent'} width="100%">
			<Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle fontSize={'16px'}>{InformationDialogTitle}</DialogTitle>
				<IconButton
					onClick={() => setModalOpen(false)}
					sx={{
						position: 'absolute',
						top: 8,
						right: 8,
					}}>
					<CloseIcon fontSize="small" />
				</IconButton>
				<DialogContent>
					<Stack gap={2.5}>
						<Typography fontSize={'14px'}>{InformationDialogText}</Typography>
						<Typography
							fontSize={'14px'}
							sx={{
								cursor: 'pointer',
								'&:hover': { textDecoration: 'underline' },
							}}
							color="primary"
							onClick={() => window.open(NAICS_LINK, '_blank')?.focus()}>
							{NAICS_LINK}
						</Typography>
					</Stack>
				</DialogContent>
			</Dialog>

			<Stack gap={3} maxWidth={650} alignSelf="center">
				<Typography textAlign="left">{questionText}</Typography>

				<Stack gap={1}>
					<RDropdown
						isLoading={isLoading}
						isValid={!isSelectInvalid}
						required
						value={selectedNAICS}
						onScrolledToBottom={handleLoadMore}
						placeholder={DropdownPlaceholder}
						errorMessage={DropdownValidationErrorMessage}
						options={options}
						setSearchValue={handleSearch}
						hasMorePages={hasMorePages}
						onSelect={handleNAICSChange}
					/>
					<Typography
						data-cy={'pqNAICSInformationLink'}
						onClick={() => setModalOpen(true)}
						sx={{
							fontSize: '12px',
							color: 'primary.main',
							cursor: 'pointer',
							'&:hover': { textDecoration: 'underline' },
						}}>
						{FindYourNAICSCodeButton}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};
